import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IoMicOffOutline,
  IoMicOutline,
  IoVolumeHighOutline,
  IoVolumeMuteOutline,
} from "react-icons/io5";
import { acquirePersistentMicrophone } from "../services/persistentMicrophone";

const VOICE_SIGNAL_EVENT = "VOICE_SIGNAL";
const VOICE_AUDIO_FRAME_EVENT = "VOICE_AUDIO_FRAME";
const MAX_VOICE_PLAYERS = 4;
const SOCKET_AUDIO_SAMPLE_RATE = 16000;
const SOCKET_AUDIO_BUFFER_SIZE = 2048;
const SOCKET_AUDIO_MAX_QUEUE_SECONDS = 0.42;
const DEFAULT_ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];
let voiceIceServersPromise = null;

const SLOT_THEME = [
  { name: "azul", rgb: "37, 99, 235", text: "#eff6ff" },
  { name: "rojo", rgb: "225, 29, 72", text: "#fff1f2" },
  { name: "amarillo", rgb: "245, 158, 11", text: "#1f1300" },
  { name: "verde", rgb: "5, 150, 105", text: "#ecfdf5" },
];

const normalizeId = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeRoomId = (value) => String(value || "").trim();

const safeJsonPayload = (rawData) => {
  if (!rawData) return {};
  if (typeof rawData === "string") {
    try {
      return JSON.parse(rawData);
    } catch {
      return {};
    }
  }
  return rawData && typeof rawData === "object" ? rawData : {};
};

const resolveSocket = () =>
  typeof window !== "undefined" ? window.__ximaSocket || window.socket || null : null;

const buildDisplayName = (player, slotIndex) =>
  String(player?.displayName || player?.name || "").trim() || `Jugador ${slotIndex + 1}`;

const removeKeyFromState = (state, key) => {
  if (!state || !Object.prototype.hasOwnProperty.call(state, key)) return state;
  const next = { ...state };
  delete next[key];
  return next;
};

const normalizeIceServer = (server) => {
  if (!server || typeof server !== "object" || Array.isArray(server)) return null;
  const urls = Array.isArray(server.urls)
    ? server.urls.map((url) => String(url || "").trim()).filter(Boolean)
    : String(server.urls || "").trim();
  if (!urls || (Array.isArray(urls) && urls.length === 0)) return null;

  const normalized = { urls };
  const username = String(server.username || "").trim();
  const credential = String(server.credential || "").trim();
  if (username && credential) {
    normalized.username = username;
    normalized.credential = credential;
  }
  return normalized;
};

const mergeIceServers = (servers) => {
  const seen = new Set();
  return [...DEFAULT_ICE_SERVERS, ...(Array.isArray(servers) ? servers : [])]
    .map(normalizeIceServer)
    .filter(Boolean)
    .filter((server) => {
      const key = JSON.stringify(server.urls);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const loadVoiceIceServers = () => {
  if (voiceIceServersPromise) return voiceIceServersPromise;
  voiceIceServersPromise = (async () => {
    if (typeof fetch !== "function") return DEFAULT_ICE_SERVERS;
    try {
      const response = await fetch("/api/voice/ice-servers", {
        cache: "no-store",
      });
      if (!response.ok) return DEFAULT_ICE_SERVERS;
      const data = await response.json();
      return mergeIceServers(data?.iceServers);
    } catch {
      return DEFAULT_ICE_SERVERS;
    }
  })();
  return voiceIceServersPromise;
};

const getAudioContextConstructor = () => {
  if (typeof window === "undefined") return null;
  return window.AudioContext || window.webkitAudioContext || null;
};

const downsampleToInt16 = (input, inputSampleRate, outputSampleRate) => {
  if (!input || !input.length || !inputSampleRate || !outputSampleRate) return null;
  const ratio = inputSampleRate / outputSampleRate;
  const outputLength = Math.max(1, Math.floor(input.length / ratio));
  const output = new Int16Array(outputLength);

  for (let index = 0; index < outputLength; index += 1) {
    const start = Math.floor(index * ratio);
    const end = Math.min(input.length, Math.floor((index + 1) * ratio));
    let sum = 0;
    let count = 0;
    for (let sourceIndex = start; sourceIndex < end; sourceIndex += 1) {
      sum += input[sourceIndex];
      count += 1;
    }
    const sample = Math.max(-1, Math.min(1, count > 0 ? sum / count : input[start] || 0));
    output[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }

  return output;
};

const toArrayBuffer = (rawFrame) => {
  if (!rawFrame) return null;
  if (rawFrame instanceof ArrayBuffer) return rawFrame;
  if (ArrayBuffer.isView(rawFrame)) {
    return rawFrame.buffer.slice(rawFrame.byteOffset, rawFrame.byteOffset + rawFrame.byteLength);
  }
  if (rawFrame?.type === "Buffer" && Array.isArray(rawFrame.data)) {
    return new Uint8Array(rawFrame.data).buffer;
  }
  return null;
};

const ensureAudioElement = (remoteKey) => {
  if (!remoteKey || typeof document === "undefined") {
    return typeof Audio !== "undefined" ? new Audio() : null;
  }

  const audio = document.createElement("audio");
  audio.autoplay = true;
  audio.controls = false;
  audio.muted = false;
  audio.volume = 1;
  audio.playsInline = true;
  audio.setAttribute("autoplay", "");
  audio.setAttribute("playsinline", "");
  audio.setAttribute("data-xima-voice-remote", remoteKey);
  audio.style.position = "fixed";
  audio.style.left = "-9999px";
  audio.style.width = "1px";
  audio.style.height = "1px";
  audio.style.opacity = "0";
  audio.style.pointerEvents = "none";
  document.body.appendChild(audio);
  return audio;
};

const shouldHandlePlayer = (players, rawUserId) => {
  const normalized = normalizeId(rawUserId);
  if (!normalized) return false;
  return players.some((player) => player.active && normalizeId(player.userId) === normalized);
};

const TournamentVoiceChat = ({
  enabled,
  tournamentId,
  roomId,
  localUserId,
  players,
  connectionState,
}) => {
  const socket = resolveSocket();
  const safePlayers = useMemo(() => {
    const source = Array.isArray(players) ? players : [];
    return Array.from({ length: MAX_VOICE_PLAYERS }, (_, index) => {
      const player = source[index] || {};
      const userId = String(player.userId || "").trim();
      return {
        slotIndex: index,
        userId,
        displayName: buildDisplayName(player, index),
        active: Boolean(player.active) && Boolean(userId),
      };
    });
  }, [players]);

  const localNormalizedId = normalizeId(localUserId);
  const localSlotIndex = safePlayers.findIndex(
    (player) => player.active && normalizeId(player.userId) === localNormalizedId
  );
  const canUseVoice =
    Boolean(enabled) &&
    Boolean(socket) &&
    Boolean(localNormalizedId) &&
    Boolean(normalizeRoomId(roomId)) &&
    Boolean(String(tournamentId || "").trim()) &&
    localSlotIndex >= 0;

  const peersRef = useRef(new Map());
  const localStreamRef = useRef(null);
  const localMicrophoneLeaseRef = useRef(null);
  const audioElementsRef = useRef(new Map());
  const iceServersRef = useRef(DEFAULT_ICE_SERVERS);
  const socketAudioCaptureRef = useRef(null);
  const socketAudioSequenceRef = useRef(0);
  const playbackAudioContextRef = useRef(null);
  const playbackScheduleByUserRef = useRef(new Map());
  const lastSocketAudioStatusAtRef = useRef(0);
  const latestRef = useRef({
    canUseVoice: false,
    micActive: false,
    tournamentId: "",
    roomId: "",
    localUserId: "",
    players: [],
    speakerMutedByUser: {},
    socket: null,
  });

  const [micActive, setMicActive] = useState(false);
  const [micStatus, setMicStatus] = useState("idle");
  const [statusText, setStatusText] = useState("");
  const [remoteMicByUser, setRemoteMicByUser] = useState({});
  const [remoteConnectedByUser, setRemoteConnectedByUser] = useState({});
  const [speakerMutedByUser, setSpeakerMutedByUser] = useState({});
  const [playbackBlockedByUser, setPlaybackBlockedByUser] = useState({});

  useEffect(() => {
    if (!enabled) return undefined;
    let canceled = false;
    loadVoiceIceServers().then((servers) => {
      if (canceled) return;
      iceServersRef.current = mergeIceServers(servers);
    });
    return () => {
      canceled = true;
    };
  }, [enabled]);

  useEffect(() => {
    latestRef.current = {
      canUseVoice,
      micActive,
      tournamentId: String(tournamentId || "").trim(),
      roomId: normalizeRoomId(roomId),
      localUserId: String(localUserId || "").trim(),
      players: safePlayers,
      speakerMutedByUser,
      socket,
    };
  }, [
    canUseVoice,
    localUserId,
    micActive,
    roomId,
    safePlayers,
    socket,
    speakerMutedByUser,
    tournamentId,
  ]);

  const emitSignal = useCallback((payload) => {
    const latest = latestRef.current;
    const signalType = String(payload?.type || "").trim().toLowerCase();
    if (
      !latest.socket ||
      !latest.tournamentId ||
      !latest.roomId ||
      !latest.localUserId ||
      (!latest.canUseVoice && signalType !== "bye")
    ) {
      return false;
    }
    latest.socket.emit(VOICE_SIGNAL_EVENT, {
      tournamentId: latest.tournamentId,
      roomId: latest.roomId,
      userId: latest.localUserId,
      ...payload,
    });
    return true;
  }, []);

  const setRemoteConnected = useCallback((remoteUserId, connected) => {
    const key = normalizeId(remoteUserId);
    if (!key) return;
    setRemoteConnectedByUser((prev) => {
      const nextConnected = Boolean(connected);
      if (prev?.[key] === nextConnected) return prev;
      return {
        ...prev,
        [key]: nextConnected,
      };
    });
  }, []);

  const cleanupPeer = useCallback(
    (remoteUserId) => {
      const remoteKey = normalizeId(remoteUserId);
      if (!remoteKey) return;

      const peer = peersRef.current.get(remoteKey);
      if (peer) {
        try {
          peer.pc.close();
        } catch {
          // peer already closed
        }
        peersRef.current.delete(remoteKey);
      }

      const audio = audioElementsRef.current.get(remoteKey);
      if (audio) {
        try {
          audio.pause();
          audio.srcObject = null;
          audio.remove();
        } catch {
          // browser cleanup only
        }
        audioElementsRef.current.delete(remoteKey);
      }

      setRemoteConnected(remoteKey, false);
      setRemoteMicByUser((prev) => {
        if (!prev || !Object.prototype.hasOwnProperty.call(prev, remoteKey)) return prev;
        const next = { ...prev };
        delete next[remoteKey];
        return next;
      });
      setPlaybackBlockedByUser((prev) => removeKeyFromState(prev, remoteKey));
    },
    [setRemoteConnected]
  );

  const setRemoteSpeakerMuted = useCallback((remoteKey, muted) => {
    if (!remoteKey) return;
    setSpeakerMutedByUser((prev) => {
      const next = { ...(prev || {}), [remoteKey]: Boolean(muted) };
      latestRef.current.speakerMutedByUser = next;
      return next;
    });

    const audio = audioElementsRef.current.get(remoteKey);
    if (audio) {
      audio.muted = Boolean(muted);
      audio.volume = muted ? 0 : 1;
    }
  }, []);

  const playRemoteAudio = useCallback(async (remoteUserId, options = {}) => {
    const remoteKey = normalizeId(remoteUserId);
    if (!remoteKey) return false;
    const audio = audioElementsRef.current.get(remoteKey);
    if (!audio) return false;

    const mutedByUser = latestRef.current.speakerMutedByUser?.[remoteKey] === true;
    if (mutedByUser && options.force !== true) return false;

    try {
      audio.muted = false;
      audio.volume = 1;
      await audio.play?.();
      setPlaybackBlockedByUser((prev) => removeKeyFromState(prev, remoteKey));
      if (options.userGesture === true) {
        setStatusText("Audio de jugadores activado.");
      }
      return true;
    } catch {
      setPlaybackBlockedByUser((prev) => ({ ...(prev || {}), [remoteKey]: true }));
      setStatusText("Toca la bocina para activar el audio.");
      return false;
    }
  }, []);

  const stopSocketAudioRelay = useCallback(() => {
    const capture = socketAudioCaptureRef.current;
    socketAudioCaptureRef.current = null;
    if (!capture) return;

    try {
      if (capture.processor) capture.processor.onaudioprocess = null;
      capture.source?.disconnect?.();
      capture.processor?.disconnect?.();
      capture.zeroGain?.disconnect?.();
    } catch {
      // best-effort audio graph cleanup
    }

    if (capture.audioContext && capture.audioContext.state !== "closed") {
      capture.audioContext.close?.().catch(() => {});
    }
  }, []);

  const releaseLocalMicrophone = useCallback(() => {
    const lease = localMicrophoneLeaseRef.current;
    localMicrophoneLeaseRef.current = null;
    localStreamRef.current = null;
    try {
      lease?.release?.();
    } catch {
      // Shared microphone cleanup is best-effort.
    }
  }, []);

  const ensurePlaybackAudioContext = useCallback(async (userGesture = false) => {
    const AudioContextConstructor = getAudioContextConstructor();
    if (!AudioContextConstructor) return null;

    let audioContext = playbackAudioContextRef.current;
    if (!audioContext || audioContext.state === "closed") {
      try {
        audioContext = new AudioContextConstructor({ sampleRate: SOCKET_AUDIO_SAMPLE_RATE });
      } catch {
        audioContext = new AudioContextConstructor();
      }
      playbackAudioContextRef.current = audioContext;
    }

    if (audioContext.state === "suspended" && userGesture) {
      await audioContext.resume?.().catch(() => {});
    }

    return audioContext;
  }, []);

  const playSocketAudioFrame = useCallback(
    async (payload) => {
      const latest = latestRef.current;
      const fromUserId = String(payload?.userId || "").trim();
      const remoteKey = normalizeId(fromUserId);
      const localKey = normalizeId(latest.localUserId);
      if (!remoteKey || remoteKey === localKey) return;
      if (!shouldHandlePlayer(latest.players, fromUserId)) return;
      if (String(payload?.tournamentId || "").trim() !== latest.tournamentId) return;
      if (normalizeRoomId(payload?.roomId) !== latest.roomId) return;
      if (latest.speakerMutedByUser?.[remoteKey] === true) return;

      const frameBuffer = toArrayBuffer(payload?.pcm || payload?.frame || payload?.audio);
      if (!frameBuffer || frameBuffer.byteLength < 2) return;

      const audioContext = await ensurePlaybackAudioContext(false);
      if (!audioContext) return;

      if (audioContext.state === "suspended") {
        setPlaybackBlockedByUser((prev) => ({ ...(prev || {}), [remoteKey]: true }));
        setStatusText("Toca una bocina para activar el audio.");
        return;
      }

      const pcm = new Int16Array(frameBuffer);
      const sampleRate = Number.isFinite(Number(payload?.sampleRate))
        ? Math.max(8000, Math.min(24000, Math.floor(Number(payload.sampleRate))))
        : SOCKET_AUDIO_SAMPLE_RATE;
      const audioBuffer = audioContext.createBuffer(1, pcm.length, sampleRate);
      const channel = audioBuffer.getChannelData(0);
      for (let index = 0; index < pcm.length; index += 1) {
        channel[index] = Math.max(-1, Math.min(1, pcm[index] / 0x8000));
      }

      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);

      const now = audioContext.currentTime;
      let nextStart = Number(playbackScheduleByUserRef.current.get(remoteKey) || 0);
      if (!Number.isFinite(nextStart) || nextStart < now) {
        nextStart = now + 0.035;
      }
      if (nextStart - now > SOCKET_AUDIO_MAX_QUEUE_SECONDS) {
        nextStart = now + 0.035;
      }

      source.start(nextStart);
      playbackScheduleByUserRef.current.set(remoteKey, nextStart + audioBuffer.duration);
      setRemoteConnected(remoteKey, true);
      setRemoteMicByUser((prev) => (prev?.[remoteKey] === true ? prev : { ...prev, [remoteKey]: true }));
      setPlaybackBlockedByUser((prev) => removeKeyFromState(prev, remoteKey));

      const statusNow = Date.now();
      if (statusNow - lastSocketAudioStatusAtRef.current > 3500) {
        lastSocketAudioStatusAtRef.current = statusNow;
        setStatusText("Voz conectada por servidor.");
      }
    },
    [ensurePlaybackAudioContext, setRemoteConnected]
  );

  const startSocketAudioRelay = useCallback(
    async (stream) => {
      const AudioContextConstructor = getAudioContextConstructor();
      const latest = latestRef.current;
      if (!AudioContextConstructor || !stream || !latest.socket) return false;

      stopSocketAudioRelay();

      let audioContext;
      try {
        try {
          audioContext = new AudioContextConstructor({ sampleRate: SOCKET_AUDIO_SAMPLE_RATE });
        } catch {
          audioContext = new AudioContextConstructor();
        }
        await audioContext.resume?.().catch(() => {});

        const source = audioContext.createMediaStreamSource(stream);
        const processor = audioContext.createScriptProcessor(SOCKET_AUDIO_BUFFER_SIZE, 1, 1);
        const zeroGain = audioContext.createGain();
        zeroGain.gain.value = 0;

        processor.onaudioprocess = (event) => {
          const current = latestRef.current;
          if (!current.socket || !current.canUseVoice || !current.micActive) return;
          const input = event.inputBuffer.getChannelData(0);
          const pcm = downsampleToInt16(input, audioContext.sampleRate, SOCKET_AUDIO_SAMPLE_RATE);
          if (!pcm || pcm.byteLength <= 0) return;
          const audioPayload = pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength);
          current.socket.emit(VOICE_AUDIO_FRAME_EVENT, {
            tournamentId: current.tournamentId,
            roomId: current.roomId,
            userId: current.localUserId,
            sampleRate: SOCKET_AUDIO_SAMPLE_RATE,
            channels: 1,
            encoding: "pcm_s16le",
            sequence: socketAudioSequenceRef.current,
            pcm: audioPayload,
          });
          socketAudioSequenceRef.current = (socketAudioSequenceRef.current + 1) % 1000000000;
        };

        source.connect(processor);
        processor.connect(zeroGain);
        zeroGain.connect(audioContext.destination);
        socketAudioCaptureRef.current = {
          audioContext,
          source,
          processor,
          zeroGain,
        };
        return true;
      } catch (error) {
        console.log(error);
        if (audioContext && audioContext.state !== "closed") {
          audioContext.close?.().catch(() => {});
        }
        return false;
      }
    },
    [stopSocketAudioRelay]
  );

  const attachLocalTrack = useCallback((remoteUserId, pc) => {
    const stream = localStreamRef.current;
    const track = stream?.getAudioTracks?.()[0] || null;
    const remoteKey = normalizeId(remoteUserId);
    if (!remoteKey || !pc) return;

    const peer = peersRef.current.get(remoteKey);
    if (!peer) return;

    if (!track) {
      if (peer.audioSender) peer.audioSender.replaceTrack(null).catch(() => {});
      return;
    }

    if (peer.audioSender) {
      peer.audioSender.replaceTrack(track).catch(() => {});
      return;
    }

    try {
      peer.audioSender = pc.addTrack(track, stream);
    } catch {
      peer.audioSender = null;
    }
  }, []);

  const ensurePeer = useCallback(
    (remoteUserId) => {
      const remoteKey = normalizeId(remoteUserId);
      if (!remoteKey) return null;
      const existing = peersRef.current.get(remoteKey);
      if (existing) return existing;

      const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
      const peer = {
        pc,
        audioSender: null,
        makingOffer: false,
        pendingCandidates: [],
      };

      try {
        if (typeof pc.addTransceiver === "function") {
          const transceiver = pc.addTransceiver("audio", { direction: "sendrecv" });
          peer.audioSender = transceiver?.sender || null;
        }
      } catch {
        peer.audioSender = null;
      }

      pc.onicecandidate = (event) => {
        if (!event.candidate) return;
        emitSignal({
          type: "ice",
          targetUserId: remoteUserId,
          candidate: event.candidate.toJSON ? event.candidate.toJSON() : event.candidate,
        });
      };

      pc.ontrack = (event) => {
        const stream = event.streams?.[0] || new MediaStream([event.track]);
        let audio = audioElementsRef.current.get(remoteKey);
        if (!audio) {
          audio = ensureAudioElement(remoteKey);
          if (!audio) return;
          audioElementsRef.current.set(remoteKey, audio);
        }

        audio.srcObject = stream;
        audio.muted = latestRef.current.speakerMutedByUser?.[remoteKey] === true;
        event.track.onunmute = () => {
          setRemoteConnected(remoteKey, true);
          playRemoteAudio(remoteKey);
        };
        playRemoteAudio(remoteKey);
        setRemoteConnected(remoteKey, true);
      };

      const updateConnectionState = () => {
        const connected = ["connected", "completed"].includes(pc.connectionState);
        const iceConnected = ["connected", "completed"].includes(pc.iceConnectionState);
        if (connected || iceConnected) {
          setRemoteConnected(remoteKey, true);
          return;
        }
        if (
          ["failed", "disconnected", "closed"].includes(pc.connectionState) ||
          ["failed", "disconnected", "closed"].includes(pc.iceConnectionState)
        ) {
          setRemoteConnected(remoteKey, false);
        }
      };
      pc.onconnectionstatechange = updateConnectionState;
      pc.oniceconnectionstatechange = updateConnectionState;

      peersRef.current.set(remoteKey, peer);
      attachLocalTrack(remoteKey, pc);
      return peer;
    },
    [attachLocalTrack, emitSignal, playRemoteAudio, setRemoteConnected]
  );

  const flushPendingCandidates = useCallback(async (peer) => {
    if (!peer?.pc?.remoteDescription || !Array.isArray(peer.pendingCandidates)) return;
    const candidates = peer.pendingCandidates.splice(0);
    await Promise.all(
      candidates.map((candidate) => peer.pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {}))
    );
  }, []);

  const handleOffer = useCallback(
    async (payload) => {
      const fromUserId = String(payload.userId || "").trim();
      const remoteKey = normalizeId(fromUserId);
      if (!remoteKey) return;

      const peer = ensurePeer(fromUserId);
      if (!peer) return;

      const { pc } = peer;
      const description = payload.sdp;
      const offerCollision = peer.makingOffer || pc.signalingState !== "stable";
      const polite = normalizeId(latestRef.current.localUserId) > remoteKey;

      try {
        if (offerCollision && !polite) return;
        if (offerCollision && polite) {
          await pc.setLocalDescription({ type: "rollback" });
        }

        await pc.setRemoteDescription(new RTCSessionDescription(description));
        attachLocalTrack(fromUserId, pc);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        emitSignal({
          type: "answer",
          targetUserId: fromUserId,
          sdp: pc.localDescription,
          micActive: latestRef.current.micActive,
        });

        await flushPendingCandidates(peer);
      } catch (error) {
        console.log(error);
        cleanupPeer(fromUserId);
      }
    },
    [attachLocalTrack, cleanupPeer, emitSignal, ensurePeer, flushPendingCandidates]
  );

  const handleAnswer = useCallback(
    async (payload) => {
      const fromUserId = String(payload.userId || "").trim();
      const peer = peersRef.current.get(normalizeId(fromUserId));
      if (!peer || !payload.sdp) return;
      try {
        if (peer.pc.signalingState !== "have-local-offer") return;
        await peer.pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
        await flushPendingCandidates(peer);
      } catch (error) {
        console.log(error);
      }
    },
    [flushPendingCandidates]
  );

  const handleIce = useCallback(
    async (payload) => {
      const fromUserId = String(payload.userId || "").trim();
      const remoteKey = normalizeId(fromUserId);
      if (!remoteKey || !payload.candidate) return;
      const peer = ensurePeer(fromUserId);
      if (!peer) return;

      try {
        if (!peer.pc.remoteDescription) {
          peer.pendingCandidates.push(payload.candidate);
          return;
        }
        await peer.pc.addIceCandidate(new RTCIceCandidate(payload.candidate));
      } catch (error) {
        console.log(error);
      }
    },
    [ensurePeer]
  );

  useEffect(() => {
    if (!canUseVoice || !socket) {
      return undefined;
    }

    const handleSignal = (rawData) => {
      const payload = safeJsonPayload(rawData);
      const latest = latestRef.current;
      if (!latest.canUseVoice) return;
      if (String(payload.tournamentId || "").trim() !== latest.tournamentId) return;
      if (normalizeRoomId(payload.roomId) !== latest.roomId) return;

      const fromUserId = String(payload.userId || "").trim();
      const fromKey = normalizeId(fromUserId);
      const localKey = normalizeId(latest.localUserId);
      if (!fromKey || fromKey === localKey) return;
      if (!shouldHandlePlayer(latest.players, fromUserId)) return;

      const targetKey = normalizeId(payload.targetUserId);
      if (targetKey && targetKey !== localKey) return;

      const type = String(payload.type || "").trim().toLowerCase();
      if (type === "hello") {
        emitSignal({
          type: "state",
          targetUserId: fromUserId,
          micActive: latest.micActive,
        });
        return;
      }

      if (type === "state") {
        setRemoteMicByUser((prev) => ({
          ...prev,
          [fromKey]: payload.micActive === true,
        }));
        return;
      }

      if (type === "bye") {
        cleanupPeer(fromUserId);
        return;
      }

      if (type === "offer") {
        setRemoteMicByUser((prev) => ({
          ...prev,
          [fromKey]: payload.micActive === true || prev[fromKey] === true,
        }));
        handleOffer(payload);
        return;
      }

      if (type === "answer") {
        setRemoteMicByUser((prev) => ({
          ...prev,
          [fromKey]: payload.micActive === true || prev[fromKey] === true,
        }));
        handleAnswer(payload);
        return;
      }

      if (type === "ice") {
        handleIce(payload);
      }
    };

    socket.on(VOICE_SIGNAL_EVENT, handleSignal);
    emitSignal({ type: "hello", micActive });
    emitSignal({ type: "state", micActive });

    return () => {
      socket.off(VOICE_SIGNAL_EVENT, handleSignal);
    };
  }, [
    canUseVoice,
    cleanupPeer,
    emitSignal,
    handleAnswer,
    handleIce,
    handleOffer,
    micActive,
    socket,
  ]);

  useEffect(() => {
    if (!canUseVoice || !socket) {
      return undefined;
    }

    const handleAudioFrame = (payload) => {
      playSocketAudioFrame(payload);
    };

    socket.on(VOICE_AUDIO_FRAME_EVENT, handleAudioFrame);
    return () => {
      socket.off(VOICE_AUDIO_FRAME_EVENT, handleAudioFrame);
    };
  }, [canUseVoice, playSocketAudioFrame, socket]);

  useEffect(() => {
    if (!canUseVoice) {
      emitSignal({ type: "bye" });
      stopSocketAudioRelay();
      releaseLocalMicrophone();
      peersRef.current.forEach((_, remoteUserId) => cleanupPeer(remoteUserId));
      setMicActive(false);
      setMicStatus("idle");
      setRemoteMicByUser({});
      setRemoteConnectedByUser({});
      setPlaybackBlockedByUser({});
    }
  }, [canUseVoice, cleanupPeer, emitSignal, releaseLocalMicrophone, stopSocketAudioRelay]);

  useEffect(() => {
    const activeRemoteKeys = new Set(
      safePlayers
        .filter((player) => player.active)
        .map((player) => normalizeId(player.userId))
        .filter((key) => key && key !== localNormalizedId)
    );

    peersRef.current.forEach((_, remoteUserId) => {
      if (!activeRemoteKeys.has(remoteUserId)) cleanupPeer(remoteUserId);
    });
  }, [cleanupPeer, localNormalizedId, safePlayers]);

  useEffect(
    () => () => {
      emitSignal({ type: "bye" });
      stopSocketAudioRelay();
      releaseLocalMicrophone();
      peersRef.current.forEach((_, remoteUserId) => cleanupPeer(remoteUserId));
      const playbackContext = playbackAudioContextRef.current;
      playbackAudioContextRef.current = null;
      playbackScheduleByUserRef.current.clear();
      if (playbackContext && playbackContext.state !== "closed") {
        playbackContext.close?.().catch(() => {});
      }
    },
    [cleanupPeer, emitSignal, releaseLocalMicrophone, stopSocketAudioRelay]
  );

  const stopMicrophone = useCallback(async () => {
    peersRef.current.forEach((peer) => {
      if (peer.audioSender) {
        peer.audioSender.replaceTrack(null).catch(() => {});
      }
    });
    stopSocketAudioRelay();
    releaseLocalMicrophone();
    latestRef.current.micActive = false;
    setMicActive(false);
    setMicStatus("idle");
    setStatusText("Microfono apagado.");
    emitSignal({ type: "state", micActive: false });
  }, [emitSignal, releaseLocalMicrophone, stopSocketAudioRelay]);

  const startMicrophone = useCallback(async () => {
    if (!canUseVoice) return;
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== "function" ||
      typeof RTCPeerConnection !== "function"
    ) {
      setMicStatus("unsupported");
      setStatusText("Microfono no disponible en este navegador.");
      return;
    }

    try {
      setMicStatus("pending");
      setStatusText("Permiso del microfono...");
      iceServersRef.current = mergeIceServers(await loadVoiceIceServers());
      const microphoneLease = await acquirePersistentMicrophone({
        windowRef: typeof window !== "undefined" ? window : null,
      });
      releaseLocalMicrophone();
      const stream = microphoneLease.stream;
      localMicrophoneLeaseRef.current = microphoneLease;
      localStreamRef.current = stream;
      stream.getAudioTracks().forEach((track) => {
        track.enabled = true;
      });

      peersRef.current.forEach((peer, remoteUserId) => attachLocalTrack(remoteUserId, peer.pc));
      latestRef.current.micActive = true;
      setMicActive(true);
      setMicStatus("active");
      const relayStarted = await startSocketAudioRelay(stream);
      setStatusText(relayStarted ? "Microfono activo por servidor." : "Microfono activo.");
      emitSignal({ type: "state", micActive: true });
    } catch (error) {
      console.log(error);
      stopSocketAudioRelay();
      releaseLocalMicrophone();
      setMicActive(false);
      setMicStatus(error?.name === "NotAllowedError" ? "denied" : "error");
      setStatusText(
        error?.name === "NotAllowedError"
          ? "Permiso del microfono rechazado."
          : "No se pudo activar el microfono."
      );
    }
  }, [
    attachLocalTrack,
    canUseVoice,
    emitSignal,
    releaseLocalMicrophone,
    startSocketAudioRelay,
    stopSocketAudioRelay,
  ]);

  const toggleMicrophone = useCallback(() => {
    if (!canUseVoice) {
      setStatusText("Entra a una sala online para usar voz.");
      return;
    }
    if (micActive) {
      stopMicrophone();
      return;
    }
    startMicrophone();
  }, [canUseVoice, micActive, startMicrophone, stopMicrophone]);

  const toggleRemoteSpeaker = useCallback((remoteUserId) => {
    const remoteKey = normalizeId(remoteUserId);
    if (!remoteKey) return;
    ensurePlaybackAudioContext(true);
    const audio = audioElementsRef.current.get(remoteKey);
    const currentlyMuted = latestRef.current.speakerMutedByUser?.[remoteKey] === true;
    const needsPlaybackUnlock =
      playbackBlockedByUser?.[remoteKey] === true || (audio && audio.paused && !currentlyMuted);

    if (currentlyMuted || needsPlaybackUnlock) {
      setRemoteSpeakerMuted(remoteKey, false);
      playRemoteAudio(remoteKey, { force: true, userGesture: true });
      return;
    }

    setRemoteSpeakerMuted(remoteKey, true);
    setStatusText("Jugador silenciado.");
  }, [ensurePlaybackAudioContext, playRemoteAudio, playbackBlockedByUser, setRemoteSpeakerMuted]);

  const statusClass = micStatus === "active" ? "is-active" : micStatus === "pending" ? "is-pending" : "";

  if (!enabled) return null;

  return (
    <div className="xima-player-voice-section">
      <div className="xima-tournament-section-title">
        <IoMicOutline />
        <span>Voz jugadores</span>
      </div>
      <div className="xima-player-voice-buttons" role="group" aria-label="Voz de jugadores">
        {safePlayers.map((player, index) => {
          const theme = SLOT_THEME[index] || SLOT_THEME[0];
          const userKey = normalizeId(player.userId);
          const isLocal = index === localSlotIndex;
          const isRemoteMuted = speakerMutedByUser[userKey] === true;
          const needsPlaybackTap = playbackBlockedByUser[userKey] === true;
          const isRemoteLive = remoteMicByUser[userKey] === true;
          const isRemoteConnected = remoteConnectedByUser[userKey] === true;
          const active = player.active && canUseVoice;
          const buttonLabel = isLocal
            ? `${player.displayName}: ${micActive ? "apagar microfono" : "activar microfono"}`
            : `${player.displayName}: ${
                needsPlaybackTap ? "activar audio" : isRemoteMuted ? "escuchar" : "silenciar"
              }`;

          return (
            <button
              key={`voice-slot-${index}`}
              type="button"
              className={`xima-player-voice-button ${active ? "is-enabled" : "is-empty"} ${
                isLocal && micActive ? "is-speaking" : ""
              } ${!isLocal && isRemoteLive && !isRemoteMuted ? "is-speaking" : ""} ${
                !isLocal && isRemoteMuted ? "is-muted" : ""
              } ${!isLocal && needsPlaybackTap ? "is-playback-blocked" : ""} ${
                !isLocal && isRemoteConnected ? "is-connected" : ""
              }`}
              style={{
                "--voice-slot-rgb": active ? theme.rgb : "111, 116, 128",
                "--voice-slot-text": active ? theme.text : "#e5e7eb",
              }}
              disabled={!active}
              onClick={isLocal ? toggleMicrophone : () => toggleRemoteSpeaker(player.userId)}
              aria-pressed={isLocal ? micActive : !isRemoteMuted}
              aria-label={buttonLabel}
              title={buttonLabel}
            >
              {isLocal ? (
                micActive ? (
                  <IoMicOutline aria-hidden="true" />
                ) : (
                  <IoMicOffOutline aria-hidden="true" />
                )
              ) : isRemoteMuted ? (
                <IoVolumeMuteOutline aria-hidden="true" />
              ) : (
                <IoVolumeHighOutline aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
      <div className={`xima-player-voice-status ${statusClass}`} role="status" aria-live="polite">
        {canUseVoice
          ? statusText || (micActive ? "Microfono activo." : "Microfono apagado.")
          : connectionState === "connected"
          ? "Esperando sala online."
          : "Reconectando voz..."}
      </div>
    </div>
  );
};

// Shared with the championship voice room, which relays the same PCM audio.
export { downsampleToInt16, getAudioContextConstructor, toArrayBuffer };

export default TournamentVoiceChat;
